/**
 * Format pivot de l'import DNS — le contrat entre les fichiers reçus et le
 * reste de la plateforme.
 *
 * Le principe : chaque fichier, quelle que soit sa forme, est ramené à ces
 * champs canoniques avant toute comparaison avec la base. On n'écrit pas un
 * parseur par entreprise, et on ne demande pas aux 406
 * entreprises de se conformer à un gabarit. Ni l'un ni l'autre ne tient.
 * Les trois canaux d'entrée — Excel, PDF, saisie de masse — convergent vers
 * ces treize champs, puis vers le même diff.
 *
 * **Seul `nom` est obligatoire.** C'est le seul champ rempli à 100 % du
 * corpus. Exiger le matricule rejetterait 20 % des lignes, la date de
 * naissance 16 % de plus. Un import qui refuse des gens qui se présenteront
 * à l'accueil est pire qu'un import incomplet.
 */
/**
 * Les treize champs canoniques, tirés du gabarit officiel.
 *
 * L'ordre est celui du gabarit : il commande l'affichage de l'outil de
 * mapping (Écran 4), où le chargé de clientèle travaille fichier en main.
 */
export const CHAMPS_CANONIQUES = [
    'matricule',
    'nom',
    'prenom',
    'fonction',
    'estAgent',
    'nbConjoint',
    'nbEnfant',
    'dateNaissance',
    'sexe',
    'lieuAffectation',
    'dateEntree',
    'dateDepart',
    /**
     * RG-FIN-01 — « la cotisation due par période est calculée sur la base
     * de la DNS : elle dépend du salaire de chaque employé ». Le brut, en
     * ariary, pour la période que couvre le fichier (voir `moisCouverts`).
     *
     * **Ce n'est pas un attribut de la personne.** Il appartient à la
     * déclaration du mois, ne se pose jamais sur le dossier patient, et ne se
     * lit qu'avec `salaire:read` — l'accueil ne le voit nulle part.
     */
    'salaireBrut',
    'estDeclareParti',
];
/**
 * `estDeclareParti` n'a **aucune colonne source** : il vient de la couleur
 * de la ligne. Il ne peut donc pas se résoudre depuis un en-tête, et
 * l'outil de mapping ne doit pas le proposer.
 *
 * C'est le champ le plus facile à oublier et le plus coûteux à manquer :
 * sans lui, des salariés partis restent éligibles.
 */
export const CHAMP_SANS_COLONNE = 'estDeclareParti';
/**
 * Des colonnes qui ne décrivent pas le salarié mais le membre de sa famille
 * listé sur une sous-ligne, sous lui : « Nom membre famille », « Prénom
 * membre famille ». Elles se désignent comme les autres, et ne s'écrivent
 * jamais sur la ligne du salarié — voir `lireLignes`.
 */
export const CHAMPS_AYANT_DROIT = ['nomAyantDroit', 'prenomAyantDroit'];
/** Les champs qu'un en-tête peut désigner. */
export const CHAMPS_RESOLUBLES = [
    ...CHAMPS_CANONIQUES.filter((champ) => champ !== CHAMP_SANS_COLONNE),
    ...CHAMPS_AYANT_DROIT,
];
export const MESSAGES_ANOMALIE = {
    NOM_ABSENT: 'Ligne sans nom : impossible de créer un dossier.',
    DATE_ILLISIBLE: "Date dans un format non reconnu. Aucune valeur n'est devinée.",
    DATE_IMPOSSIBLE: 'Date inexistante au calendrier (31 février, 13e mois…).',
    ANNEE_SUR_DEUX_CHIFFRES: 'Année sur deux chiffres : 62 peut être 1962 ou 2062. À compléter à la main.',
    ANNEE_COMPLETEE: 'Année sur deux chiffres complétée (âge plausible, ou entrée pas dans le futur) : à confirmer.',
    JOUR_MOIS_INVERSES: 'Jour et mois inversés (la feuille écrit le mois avant le jour) : date proposée, à confirmer.',
    SEXE_INCONNU: 'Valeur de sexe non reconnue.',
    COMPTEUR_IMPRECIS: '« X » vaut « au moins un » : le nombre exact est inconnu.',
    COMPTEUR_ILLISIBLE: 'Nombre de conjoints ou d’enfants impossible (au-delà de 30) : champ laissé vide.',
    MONTANT_ILLISIBLE: 'Salaire illisible ou impossible : aucun montant n’est deviné, la cotisation de cette ligne reste à établir.',
    TOTAL_TRIMESTRE_DIFFERENT: 'Les trois mois ne font pas le total trimestriel du fichier : chaque mois est gardé, la ligne est à vérifier.',
};
//# sourceMappingURL=pivot.js.map